import { Layout } from "@/components/Layout";
import { IMAGES } from "@/assets/images";
import { Button } from "@/components/ui/button";
import { ArrowRight, Brain, Cloud, Shield, Zap } from "lucide-react";
import { motion } from "framer-motion";
import { springPresets, fadeInUp, staggerContainer, staggerItem } from "@/lib/motion";
import { Link } from "react-router-dom";
import { ROUTE_PATHS } from "@/lib";

export default function Home() {
  return (
    <Layout>
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={IMAGES.TEAM_HERO_2}
            alt="ALTCloud AI Team"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-transparent to-background/70" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            className="max-w-4xl mx-auto text-center"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={springPresets.gentle}
          >
            <motion.div
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-8"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={springPresets.snappy}
            >
              <Brain className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium text-primary">AI-Led IT Services</span>
            </motion.div>

            <h1 className="text-5xl md:text-7xl font-bold mb-6 tracking-tight">
              Transform Your Business with{" "}
              <span className="bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent">
                AI-Powered Solutions
              </span>
            </h1>

            <p className="text-xl md:text-2xl text-muted-foreground mb-12 max-w-3xl mx-auto">
              ALTCloud Canada delivers cutting-edge IT services powered by artificial intelligence,
              helping businesses across Canada innovate and scale with confidence.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to={ROUTE_PATHS.SERVICES}>
                <Button size="lg" className="group">
                  Explore Services
                  <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
              <Link to={ROUTE_PATHS.CONTACT}>
                <Button size="lg" variant="outline">
                  Get in Touch
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {[
              {
                icon: Brain,
                title: "AI-Powered",
                description: "Leverage cutting-edge artificial intelligence to drive innovation",
              },
              {
                icon: Cloud,
                title: "Cloud Solutions",
                description: "Scalable cloud infrastructure tailored to your business needs",
              },
              {
                icon: Shield,
                title: "Enterprise Security",
                description: "Bank-grade security protocols protecting your critical data",
              },
              {
                icon: Zap,
                title: "Rapid Deployment",
                description: "Fast implementation with minimal disruption to operations",
              },
            ].map((feature, index) => (
              <motion.div
                key={index}
                variants={staggerItem}
                className="bg-card p-8 rounded-2xl border border-border shadow-lg hover:shadow-xl transition-shadow"
              >
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-6">
                  <feature.icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-3">{feature.title}</h3>
                <p className="text-muted-foreground">{feature.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={IMAGES.AI_BACKGROUND_3}
            alt="AI Technology"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background via-background/80 to-background" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            className="max-w-3xl mx-auto text-center mb-16"
            variants={fadeInUp}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Why Choose ALTCloud Canada?
            </h2>
            <p className="text-xl text-muted-foreground">
              With over 60 years of combined experience, our leadership team brings unparalleled
              expertise in sales, technology, business development, and IT operations.
            </p>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-3 gap-8"
          >
            {[
              {
                stat: "60+",
                label: "Years of Leadership",
                description: "Proven track record in technology and business development",
              },
              {
                stat: "100%",
                label: "Canadian Owned",
                description: "Proudly serving businesses across Ontario and beyond",
              },
              {
                stat: "24/7",
                label: "Support Available",
                description: "Round-the-clock assistance for your critical operations",
              },
            ].map((item, index) => (
              <motion.div
                key={index}
                variants={staggerItem}
                className="bg-card/50 backdrop-blur-sm p-8 rounded-2xl border border-border text-center"
              >
                <div className="text-5xl font-bold text-primary mb-2">{item.stat}</div>
                <div className="text-lg font-semibold mb-3">{item.label}</div>
                <p className="text-muted-foreground">{item.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="py-24 bg-primary text-primary-foreground">
        <div className="container mx-auto px-4">
          <motion.div
            className="max-w-4xl mx-auto text-center"
            variants={fadeInUp}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Ready to Transform Your Business?
            </h2>
            <p className="text-xl mb-8 opacity-90">
              Let's discuss how ALTCloud Canada can help you leverage AI and cloud technologies
              to achieve your business goals.
            </p>
            <Link to={ROUTE_PATHS.CONTACT}>
              <Button size="lg" variant="secondary" className="group">
                Schedule a Consultation
                <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}